import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Brain, Cpu, CheckCircle, XCircle, Loader2, Rocket, Boxes } from 'lucide-react';
import { models, skus } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { MetricCard } from '../components/MetricCard';
import type { TrainingJob, TrainingStatus } from '../types';

const ACTIVE_STATUSES: TrainingStatus[] = ['queued', 'launching', 'training', 'quantising'];

// Poll only while something is actually in flight. The driver writes progress at
// most once per epoch (minutes apart), so 30s is already finer-grained than the
// data — and an idle Models page must not generate traffic forever. Exported so
// the predicate is covered directly: the page's useQuery is mocked in tests.
export function pollIntervalFor(data: unknown): number | false {
  const list = Array.isArray(data) ? (data as TrainingJob[]) : [];
  return list.some((j) => ACTIVE_STATUSES.includes(j.status)) ? 30_000 : false;
}

function formatEta(ms: number): string {
  if (!Number.isFinite(ms) || ms <= 0) return 'any moment';
  const mins = Math.round(ms / 60_000);
  return mins < 60 ? `~${mins}m left` : `~${Math.floor(mins / 60)}h ${mins % 60}m left`;
}

// The job record only moves when an epoch COMPLETES, and an epoch runs for several
// minutes — so the bar and the ETA are derived at render time rather than stored.
// A job sitting on epoch 61 for four minutes should show a shrinking ETA, not a
// frozen one. Rate comes from started_at → progress.at (the measured per-epoch cost
// of this run), never from a guess.
function TrainingProgressBar({ job }: { job: TrainingJob }) {
  const p = job.progress;
  if (!p || p.total_epochs <= 0) return null;

  const pct = Math.min(100, Math.round((p.epoch / p.total_epochs) * 100));
  let eta: string | null = null;
  if (job.started_at && p.epoch > 0 && p.epoch < p.total_epochs) {
    const perEpochMs = (Date.parse(p.at) - Date.parse(job.started_at)) / p.epoch;
    if (Number.isFinite(perEpochMs) && perEpochMs > 0) {
      const finishAt = Date.parse(p.at) + perEpochMs * (p.total_epochs - p.epoch);
      eta = formatEta(finishAt - Date.now());
    }
  }

  return (
    <div className="mt-1.5 w-28">
      <div className="h-1 overflow-hidden rounded-full bg-surface-3">
        <div className="h-full bg-amber-400 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-0.5 text-[10px] tabular-nums text-gray-500">
        epoch {p.epoch}/{p.total_epochs}
        {eta ? ` · ${eta}` : ''}
      </div>
    </div>
  );
}

const STATUS_CONFIG: Record<TrainingStatus, { icon: React.ReactNode; color: string }> = {
  queued: { icon: <Loader2 size={12} className="animate-spin" />, color: 'text-gray-400' },
  launching: { icon: <Cpu size={12} className="animate-pulse" />, color: 'text-blue-400' },
  training: { icon: <Cpu size={12} className="animate-pulse" />, color: 'text-amber-400' },
  quantising: { icon: <Cpu size={12} className="animate-pulse" />, color: 'text-purple-400' },
  completed: { icon: <CheckCircle size={12} />, color: 'text-emerald-400' },
  failed: { icon: <XCircle size={12} />, color: 'text-red-400' },
  canceled: { icon: <XCircle size={12} />, color: 'text-gray-500' },
};

export default function ModelsPage() {
  const { user } = useAuth();

  const { data: jobs = [], isLoading, error } = useQuery({
    queryKey: ['models', 'jobs'],
    queryFn: models.listJobs,
    refetchInterval: (query) => pollIntervalFor(query.state.data),
  });

  const queryClient = useQueryClient();
  const registerModel = useMutation({
    mutationFn: (job: TrainingJob) => skus.registerModel(job.sku_id, job.id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['skus'] }),
  });

  if (error) {
    return (
      <div className="p-8 text-center">
        <p className="text-sm text-red-400 mb-2">Failed to load training jobs</p>
        <p className="text-xs text-gray-500">{(error as Error).message}</p>
        <button onClick={() => window.location.reload()} className="btn-primary mt-4 text-sm">
          Reload Page
        </button>
      </div>
    );
  }

  const jobsList = Array.isArray(jobs) ? (jobs as TrainingJob[]) : [];
  const canTrain = user?.org_type === 'kinetica' || user?.org_type === 'integrator';
  const activeJobs = jobsList.filter((j) => ACTIVE_STATUSES.includes(j.status));
  const completedJobs = jobsList.filter((j) => j.status === 'completed');

  if (!canTrain) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <Brain className="text-gray-600 mb-4" size={48} />
        <h2 className="text-lg font-semibold text-white mb-2">Edge Model Training</h2>
        <p className="text-sm text-gray-500 text-center max-w-md">
          Model training is managed by your integrator. Contact them to request a custom model
          trained on your specific use case.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-semibold text-white">Edge Model Training</h2>
        <Link to="/skus" className="btn-primary text-sm flex items-center gap-2">
          <Rocket size={14} /> New Training Run
        </Link>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <MetricCard label="Total Jobs" value={jobsList.length} />
        <MetricCard label="Active" value={activeJobs.length} accent="text-amber-400" />
        <MetricCard label="Completed" value={completedJobs.length} accent="text-emerald-400" />
        <MetricCard label="Cost / Run" value="$12" sub="~$2 compute + $10 service" />
      </div>

      {/* Training pipeline visualisation */}
      <div className="card mb-6">
        <h3 className="text-sm font-semibold text-gray-300 mb-3">Training Pipeline</h3>
        <div className="flex items-center gap-2 overflow-x-auto pb-2">
          {['Queue Job', 'GPU Worker Train', 'S3 Model', 'Quantise (QCS6490)', 'Greengrass OTA', 'Edge Deploy'].map(
            (step, i) => (
              <div key={step} className="flex items-center gap-2">
                {i > 0 && <span className="text-gray-600 text-xs">-&gt;</span>}
                <div className="bg-surface-2 border border-surface-4 rounded-lg px-3 py-2 text-xs text-gray-300 whitespace-nowrap">
                  {step}
                </div>
              </div>
            ),
          )}
        </div>
        <p className="text-[11px] text-gray-600 mt-2">
          yolov8n-seg fine-tuned on a GPU worker that drains the queue, then quantised for the Dragon
          NPU. Requires ml_vision entitlement.
        </p>
      </div>

      {/* Jobs table */}
      <div className="card overflow-hidden p-0 overflow-x-auto">
        <div className="px-5 py-3 border-b border-surface-3">
          <h3 className="text-sm font-semibold text-gray-300">Training Jobs</h3>
        </div>

        {isLoading ? (
          <div className="p-8 text-center text-gray-500 text-sm">Loading jobs...</div>
        ) : jobsList.length === 0 ? (
          <div className="p-8 text-center">
            <Brain className="mx-auto mb-3 text-gray-600" size={32} />
            <p className="text-sm text-gray-400">No training jobs yet</p>
            <p className="text-xs text-gray-600 mt-1">
              Queue a training run from a SKU that has a captured dataset.
            </p>
          </div>
        ) : (
          <table className="w-full">
            <thead>
              <tr className="border-b border-surface-3">
                <th className="table-header table-cell text-left">Status</th>
                <th className="table-header table-cell text-left">Job ID</th>
                <th className="table-header table-cell text-left">SKU</th>
                <th className="table-header table-cell text-left">Dataset</th>
                {user?.org_type === 'kinetica' && (
                  <th className="table-header table-cell text-left">Organisation</th>
                )}
                <th className="table-header table-cell text-right">Images</th>
                <th className="table-header table-cell text-right">mAP50</th>
                <th className="table-header table-cell text-left">Created</th>
                <th className="table-header table-cell text-right">Actions</th>
              </tr>
            </thead>
            <tbody>
              {jobsList.map((job: TrainingJob) => {
                const cfg = STATUS_CONFIG[job.status as TrainingStatus] ?? {
                  icon: <XCircle size={12} />,
                  color: 'text-gray-400',
                };
                return (
                  <tr key={job.id} className="hover:bg-surface-2/50">
                    <td className="table-cell">
                      <span className={`inline-flex items-center gap-1.5 text-xs font-medium ${cfg.color}`}>
                        {cfg.icon} {job.status}
                      </span>
                      {job.status === 'training' && <TrainingProgressBar job={job} />}
                    </td>
                    <td className="table-cell font-mono text-xs text-kinetica-400">
                      {job.id ? job.id.slice(0, 8) : 'n/a'}
                    </td>
                    <td className="table-cell text-xs text-gray-300">{job.sku_code}</td>
                    <td className="table-cell font-mono text-xs text-gray-400">{job.dataset_version}</td>
                    {user?.org_type === 'kinetica' && (
                      <td className="table-cell text-xs text-gray-400">{job.org_id}</td>
                    )}
                    <td className="table-cell text-right text-xs text-gray-400">{job.image_count}</td>
                    <td className="table-cell text-right font-mono text-xs">
                      {job.metrics ? `${(job.metrics.map50 * 100).toFixed(1)}%` : '—'}
                    </td>
                    <td className="table-cell text-xs text-gray-500">{job.created_at}</td>
                    <td className="table-cell text-right">
                      {job.status === 'completed' && job.model_key ? (
                        <button
                          onClick={() => registerModel.mutate(job)}
                          disabled={registerModel.isPending}
                          className="text-xs inline-flex items-center gap-1 text-kinetica-400 hover:text-kinetica-300 disabled:opacity-50"
                          title="Register the trained ONNX model into this SKU's registry"
                        >
                          <Boxes size={12} /> Register
                        </button>
                      ) : (
                        <span className="text-gray-600 text-xs">—</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
